<template>
  <div>
    <OverrideDefaultValue
      v-if="!hasDefaultDateCrossedThreshold"
      v-slot="{ defaultInputBinds }"
      v-model="customDate"
      :label="label"
      :default-value="defaultDate"
      :formatted-default-value="formattedDefaultDate"
      :initial-overriden-value="initialDate"
      :restore-default-text="t('useDefault')"
    >
      <Datepicker
        :date-format="activeDateFormat"
        :enable-time-picker="enableTimePicker"
        :clearable="false"
        v-bind="defaultInputBinds"
        :hint="hint"
        :is-error="isError"
      />
    </OverrideDefaultValue>

    <Datepicker
      v-else
      v-model="customDate"
      :label="label"
      :date-format="activeDateFormat"
      :enable-time-picker="enableTimePicker"
      :clearable="false"
      :hint="hint"
      :is-error="isError"
    />
  </div>
</template>

<script setup lang="ts">
import Datepicker from '@/components/Datepicker.vue';
import OverrideDefaultValue from '../OverrideDefaultValue.vue';
import { max, min } from 'date-fns';
import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue';
import { useDateFormat, useDateTimeFormat } from '@/hooks/use-date-format';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    thresholdDate: string;
    initialDate?: string | null;
    thresholdMode?: 'max' | 'min';
    label?: string;
    enableTimePicker?: boolean;
    hint?: string;
    isError?: boolean;
  }>(),
  {
    thresholdMode: 'max',
  },
);

const emit = defineEmits<{
  (event: 'dateChange', date: string): void;
}>();

const dateTimeFormat = useDateTimeFormat();
const dateFormat = useDateFormat();
const activeDateFormat = computed(() =>
  props.enableTimePicker ? dateTimeFormat.value : dateFormat.value,
);

function getDefaultDate() {
  const dates = [new Date(), new Date(props.thresholdDate)];
  return props.thresholdMode === 'max' ? min(dates).toISOString() : max(dates).toISOString();
}

const defaultDate = ref(getDefaultDate());
const formattedDefaultDate = useFormattedDate(defaultDate, activeDateFormat);
const hasDefaultDateCrossedThreshold = computed(() =>
  props.thresholdMode === 'max'
    ? new Date(defaultDate.value) >= new Date(props.thresholdDate)
    : new Date(defaultDate.value) <= new Date(props.thresholdDate),
);

const customDate = ref(
  props.initialDate ?? (!hasDefaultDateCrossedThreshold.value ? null : defaultDate.value) ?? null,
);

onMounted(() => {
  const interval = setInterval(() => {
    defaultDate.value = getDefaultDate();
  }, 60_000);
  onUnmounted(() => clearInterval(interval));
});

watchEffect(() => {
  emit('dateChange', customDate.value ?? defaultDate.value);
});
</script>

<i18n lang="json">
{
  "en": {
    "useDefault": "Use default"
  },
  "de": {
    "useDefault": "Standard verwenden"
  },
  "es": {
    "useDefault": "Usar valor predeterminado"
  }
}
</i18n>
